<template>
    <n-dialog :visible="props.visible" :close-on-press-escape="!state?.submitting" :close-on-click-modal="!state?.submitting" @update:visible="onVisibleChange">
        <div class="cancel-translate-content" :class="{ 'desktop-cancel-translate-content': isDesktop }" v-if="state">
            <div v-if="isShift || state.charsChanged" class="cancel-translate-content-banner">
                {{ $t(bannerKey) }}
            </div>
            <div class="cancel-translate-content-title">{{ $t(titleKey) }}</div>
            <div class="cancel-translate-content-file">
                <span class="file-name">{{ state.fileName }}</span>
                <span class="stage-tag" :class="stageTagClass">{{ $t(stageTagKey) }}</span>
            </div>
            <div class="cancel-translate-content-desc">{{ $t(descKey) }}</div>

            <div v-if="isParseLike" class="cancel-translate-content-cost">
                <div class="cost-formula">
                    {{ $t('cancel_dlg_page_formula', { pageCount: formatNum(state.pageCount), charsPerPage: formatNum(state.charsPerPage) }) }}
                </div>
                <div class="cost-total">{{ $t('cancel_dlg_total_chars', { chars: formatNum(state.shouldCostChars) }) }}</div>
                <div class="cost-tip">{{ $t('cancel_dlg_no_debt_tip') }}</div>
            </div>
            <div v-else-if="isTranslateLike" class="cancel-translate-content-cost cancel-translate-content-cost--warn">
                <div class="cost-formula">{{ $t('cancel_dlg_deducted_label') }}</div>
                <div class="cost-total">{{ $t('cancel_dlg_total_chars', { chars: formatNum(state.costCharsNum) }) }}</div>
                <div class="cost-tip">{{ $t('cancel_dlg_no_refund_tip') }}</div>
            </div>

            <div class="cancel-translate-content-button">
                <template v-if="state.variant === 'queue'">
                    <div class="cancel-translate-content-button-item_cancel" :class="{ disabled: state.submitting }" @click="onKeepClick">
                        <div class="cancel-translate-content-button-item_cancel_text">{{ $t('cancel_dlg_keep') }}</div>
                    </div>
                    <div class="cancel-translate-content-button-item_confirm" :class="{ disabled: state.submitting }" @click="onConfirmClick">
                        <div class="cancel-translate-content-button-item_confirm_text">{{ $t('cancel_dlg_confirm') }}</div>
                    </div>
                </template>
                <template v-else>
                    <div class="cancel-translate-content-button-item_cancel" :class="{ disabled: state.submitting }" @click="onConfirmClick">
                        <div class="cancel-translate-content-button-item_cancel_text">{{ $t('cancel_dlg_still_cancel') }}</div>
                    </div>
                    <div class="cancel-translate-content-button-item_confirm" :class="{ disabled: state.submitting }" @click="onKeepClick">
                        <div class="cancel-translate-content-button-item_confirm_text">{{ $t('cancel_dlg_keep') }}</div>
                    </div>
                </template>
            </div>
        </div>
    </n-dialog>
</template>

<script setup lang="ts">
import type { CancelDialogState } from '~/composables/useCancelTranslate'

const props = defineProps<{ visible: boolean; state: CancelDialogState | null }>()
const emit = defineEmits<{ (e: 'update:visible', v: boolean): void; (e: 'confirm'): void; (e: 'keep'): void }>()

const viewport = useViewport()
const isDesktop = computed(() => viewport.breakpoint?.value.includes('desktop'))

const TITLE_KEYS: Record<string, string> = {
    queue: 'cancel_dlg_queue_title',
    parse: 'cancel_dlg_parse_title',
    translate: 'cancel_dlg_translate_title',
    shiftToParse: 'cancel_dlg_shift_parse_title',
    shiftToTranslate: 'cancel_dlg_shift_translate_title',
}
const DESC_KEYS: Record<string, string> = {
    queue: 'cancel_dlg_queue_desc',
    parse: 'cancel_dlg_parse_desc',
    translate: 'cancel_dlg_translate_desc',
    shiftToParse: 'cancel_dlg_shift_parse_desc',
    shiftToTranslate: 'cancel_dlg_shift_translate_desc',
}
const STAGE_TAG_KEYS: Record<number, string> = {
    1: 'cancel_stage_tag_queue',
    2: 'cancel_stage_tag_parse',
    3: 'cancel_stage_tag_translate',
}
// 跳变提示条文案：仅 shiftToParse / shiftToTranslate 两种形态命中，展示“确认瞬间阶段已变化”
const BANNER_KEYS: Record<string, string> = {
    shiftToParse: 'cancel_dlg_shift_parse_banner',
    shiftToTranslate: 'cancel_dlg_shift_translate_banner',
}
// 阶段没变、只有应扣字符数变了（后端同样抛 20045）时的提示条文案
const CHARS_CHANGED_BANNER_KEY = 'cancel_dlg_chars_changed_banner'

const titleKey = computed(() => TITLE_KEYS[props.state?.variant ?? 'queue'])
const descKey = computed(() => DESC_KEYS[props.state?.variant ?? 'queue'])
const stageTagKey = computed(() => STAGE_TAG_KEYS[props.state?.stage ?? 1])
const isParseLike = computed(() => props.state?.variant === 'parse' || props.state?.variant === 'shiftToParse')
const isTranslateLike = computed(() => props.state?.variant === 'translate' || props.state?.variant === 'shiftToTranslate')
const isShift = computed(() => props.state?.variant === 'shiftToParse' || props.state?.variant === 'shiftToTranslate')
const bannerKey = computed(() => (props.state?.charsChanged ? CHARS_CHANGED_BANNER_KEY : BANNER_KEYS[props.state?.variant ?? ''] ?? ''))
// 状态标签配色：排队用中性灰，解析/翻译用主色
const stageTagClass = computed(() => (props.state?.stage === 1 ? 'stage-tag-queue' : 'stage-tag-active'))

function formatNum(n: number | null) {
    return n === null ? '-' : n.toLocaleString()
}

/** n-dialog 的 ESC / 遮罩关闭（NDialog 默认 showClose=false，无 ✕）统一走这里：与「继续翻译」语义一致 */
function onVisibleChange(v: boolean) {
    if (!v) {
        emit('keep')
    }
    emit('update:visible', v)
}

function onKeepClick() {
    if (props.state?.submitting) return
    emit('keep')
}

function onConfirmClick() {
    if (props.state?.submitting) return
    emit('confirm')
}
</script>

<style lang="scss" scoped>
.desktop-cancel-translate-content {
    width: 440px;
    padding: 24px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    .cancel-translate-content-banner {
        box-sizing: border-box;
        width: 100%;
        padding: 8px 12px;
        margin-bottom: 12px;
        font-size: 12px;
        line-height: 18px;
        color: #d46b08;
        font-weight: 600;
        background-color: #fff7e6;
        border-radius: 4px;
    }

    .cancel-translate-content-title {
        font-size: 16px;
        font-weight: bold;
        color: #262626;
    }

    .cancel-translate-content-file {
        margin-top: 16px;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        padding: 12px;
        background-color: #f5f7fa;
        border-radius: 4px;

        .file-name {
            flex: 1 1 0;
            font-size: 13px;
            color: #262626;
            font-weight: 600;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .stage-tag {
            flex-shrink: 0;
            padding: 2px 8px;
            font-size: 11px;
            line-height: 18px;
            white-space: nowrap;
            border-radius: 4px;
        }

        .stage-tag-queue {
            color: #595959;
            background-color: #f0f0f0;
        }

        .stage-tag-active {
            color: #2590e1;
            background-color: #e6f4ff;
        }
    }

    .cancel-translate-content-desc {
        margin-top: 12px;
        font-size: 13px;
        line-height: 20px;
        color: #595959;
    }

    .cancel-translate-content-cost {
        margin-top: 12px;
        box-sizing: border-box;
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 12px;
        background-color: #f5f7fa;
        border-radius: 4px;

        .cost-formula {
            font-size: 13px;
            color: #595959;
        }

        .cost-total {
            font-size: 15px;
            font-weight: bold;
            color: #262626;
        }

        .cost-tip {
            font-size: 12px;
            color: #8c8c8c;
        }
    }

    .cancel-translate-content-cost--warn {
        background-color: #fff7e6;

        .cost-formula,
        .cost-total {
            color: #d46b08;
        }

        .cost-tip {
            color: #d46b08;
        }
    }

    .cancel-translate-content-button {
        margin-top: 24px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        gap: 10px;

        .cancel-translate-content-button-item_cancel {
            width: 50%;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #fff;
            border: 1px solid #2590e1;
            border-radius: 4px;
            cursor: pointer;

            .cancel-translate-content-button-item_cancel_text {
                font-size: 14px;
                color: #2590e1;
            }

            &:hover {
                opacity: 0.7;
            }

            &.disabled {
                cursor: not-allowed;
                opacity: 0.6;
            }
        }

        .cancel-translate-content-button-item_confirm {
            width: 50%;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #2590e1;
            border-radius: 4px;
            cursor: pointer;

            .cancel-translate-content-button-item_confirm_text {
                font-size: 14px;
                color: #fff;
            }

            &:hover {
                opacity: 0.7;
            }

            &.disabled {
                cursor: not-allowed;
                opacity: 0.6;
            }
        }
    }
}
</style>
